// ─── Groups / Invites / Dog-ownership Query Hooks ───────────────────
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { queryKeys } from '@/lib/queryKeys';
import {
  createGroup, listMyGroups, getGroup, deleteGroup,
  addGroupMember, removeGroupMember, listGroupTrails,
  createInvite, listGroupInvites, revokeInvite,
  listMyInvites, acceptInvite,
  listDogOwners, addDogOwner, removeDogOwner,
  setTrailVisibility as setTrailVisibilityAPI,
} from '@/lib/api/groups';
import { patchVisibilityEverywhere } from '@/lib/queries/trails';
import { fetchInviteSuggestions } from '@/lib/api/groups';
import type { GroupType, MemberRole, Visibility } from '@/types/group';

export function useMyGroups(token: string | undefined, type?: GroupType) {
  return useQuery({
    queryKey: queryKeys.groups.mine(type),
    queryFn: () => listMyGroups(token!, type),
    enabled: !!token,
    staleTime: 30_000,
  });
}

export function useGroup(token: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.groups.detail(id ?? ''),
    queryFn: () => getGroup(token!, id!),
    enabled: !!token && !!id,
  });
}

export function useGroupTrails(token: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.groups.trails(id ?? ''),
    queryFn: () => listGroupTrails(token!, id!),
    enabled: !!token && !!id,
  });
}

export function useGroupInvites(token: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.groups.invites(id ?? ''),
    queryFn: () => listGroupInvites(token!, id!),
    enabled: !!token && !!id,
  });
}

export function useMyInvites(token: string | undefined) {
  return useQuery({
    queryKey: queryKeys.invites.mine,
    queryFn: () => listMyInvites(token!),
    enabled: !!token,
    staleTime: 30_000,
  });
}

export function useCreateGroup(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, type }: { name: string; type: GroupType }) => {
      if (!token) throw new Error('No session');
      return createGroup(token, name, type);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups', 'mine'] });
      qc.invalidateQueries({ queryKey: queryKeys.invites.mine });
    },
  });
}

export function useDeleteGroup(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!token) throw new Error('No session');
      return deleteGroup(token, id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups', 'mine'] }),
  });
}

export function useAddMember(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId, role }: { groupId: string; userId: string; role?: MemberRole }) => {
      if (!token) throw new Error('No session');
      return addGroupMember(token, groupId, userId, role ?? 'member');
    },
    onSuccess: (_data, { groupId }) => qc.invalidateQueries({ queryKey: queryKeys.groups.detail(groupId) }),
  });
}

export function useRemoveMember(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) => {
      if (!token) throw new Error('No session');
      return removeGroupMember(token, groupId, userId);
    },
    onSuccess: (_data, { groupId }) => qc.invalidateQueries({ queryKey: queryKeys.groups.detail(groupId) }),
  });
}

export function useCreateInvite(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, email, role }: { groupId: string; email: string; role?: MemberRole }) => {
      if (!token) throw new Error('No session');
      return createInvite(token, groupId, email, role ?? 'member');
    },
    onSuccess: (_data, { groupId }) => qc.invalidateQueries({ queryKey: queryKeys.groups.invites(groupId) }),
  });
}

export function useRevokeInvite(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, inviteId }: { groupId: string; inviteId: string }) => {
      if (!token) throw new Error('No session');
      return revokeInvite(token, groupId, inviteId);
    },
    onSuccess: (_data, { groupId }) => qc.invalidateQueries({ queryKey: queryKeys.groups.invites(groupId) }),
  });
}

export function useAcceptInvite(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => {
      if (!token) throw new Error('No session');
      return acceptInvite(token, code);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups', 'mine'] });
      qc.invalidateQueries({ queryKey: queryKeys.invites.mine });
    },
  });
}

export function useDogOwners(token: string | undefined, dogId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.dogs.owners(dogId ?? ''),
    queryFn: () => listDogOwners(token!, dogId!),
    enabled: !!token && !!dogId,
  });
}

export function useAddDogOwner(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dogId, userId }: { dogId: string; userId: string }) => {
      if (!token) throw new Error('No session');
      return addDogOwner(token, dogId, userId);
    },
    onSuccess: (_data, { dogId }) => qc.invalidateQueries({ queryKey: queryKeys.dogs.owners(dogId) }),
  });
}

export function useRemoveDogOwner(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dogId, userId }: { dogId: string; userId: string }) => {
      if (!token) throw new Error('No session');
      return removeDogOwner(token, dogId, userId);
    },
    onSuccess: (_data, { dogId }) => qc.invalidateQueries({ queryKey: queryKeys.dogs.owners(dogId) }),
  });
}

export function useSetTrailVisibility(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ trailId, visibility, sharedGroupId }: { trailId: string; visibility: Visibility; sharedGroupId?: string | null }) => {
      if (!token) throw new Error('No session');
      return setTrailVisibilityAPI(token, trailId, visibility, sharedGroupId);
    },
    onSuccess: (data, { trailId }) => {
      // Surgical: patch visibility fields in every cached view (lists + that
      // trail's detail variants), then refetch its details for the shared
      // group name. Group screens refetch via prefix.
      patchVisibilityEverywhere(qc, trailId, data?.visibility, data?.sharedGroupId);
      qc.invalidateQueries({ queryKey: ['trails', 'detail', trailId] });
      qc.invalidateQueries({ queryKey: ['groups', 'trails'] });
    },
  });
}
/** Debounced (350 ms) typeahead for the invite inputs. */
export function useInviteSuggestions(token: string | undefined, groupId: string | undefined, query: string) {
  const trimmed = query.trim();
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), 350);
    return () => clearTimeout(t);
  }, [trimmed]);

  return useQuery({
    queryKey: ['groups', 'invite-suggestions', groupId, debounced],
    queryFn: () => fetchInviteSuggestions(token!, groupId!, debounced),
    enabled: !!token && !!groupId && debounced.length >= 3,
    staleTime: 30_000,
  });
}
